import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination, Autoplay } from "swiper/modules";
import ReminderManager from "./ReminderManager";
import UPIReminderModal from "./UPIReminderModal";
import TermsReminderModal from "./TermsReminderModal";

const Home = () => {
  const navigate = useNavigate();
  const server_url = localStorage.getItem("server_url");
  const server_folder = localStorage.getItem("server_folder");
  const [insuranceData, setInsuranceData] = useState([]);
  const [insuranceLoading, setInsuranceLoading] = useState(true);
  const [jobCards, setJobCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collectionData, setCollectionData] = useState({
    currentMonth: { totalCars: 0, amount: 0 },
    lastThreeMonths: [],
  });
  const [collectionLoading, setCollectionLoading] = useState(true);
  const [showUPIReminder, setShowUPIReminder] = useState(false);
  const [showTermsReminder, setShowTermsReminder] = useState(false);
  const [hasTermsData, setHasTermsData] = useState(false);
  const [garageUPI, setGarageUPI] = useState("");
  const location = useLocation();

  // Cache helpers
  const getCachedData = (key, maxAge) => {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < maxAge) {
      return data;
    } else {
      localStorage.removeItem(key);
      return null;
    }
  };

  const setCachedData = (key, data) => {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  };

  useEffect(() => {
    // if url has dynamic ID /home/:id then redirect after 3 seconds
    if (location.pathname.startsWith("/home/")) {
      setTimeout(() => {
        navigate("/");
      }, 2000);
    }
  }, [location, navigate]);

  const handleEditInsurance = (insurance) => {
    navigate("/edit-insurance", { state: { insurance } });
  };

  useEffect(() => {
    // Load cached data first and set states/loading accordingly
    const cachedJobCards = getCachedData("jobCards", 60000); // 1 min for vehicles
    setJobCards(cachedJobCards || []);
    setLoading(!!cachedJobCards ? false : true);

    const cachedCollection = getCachedData("collectionData", 600000); // 10 min for collections
    setCollectionData(
      cachedCollection || {
        currentMonth: { totalCars: 0, amount: 0 },
        lastThreeMonths: [],
      }
    );
    setCollectionLoading(!!cachedCollection ? false : true);

    const cachedInsurance = getCachedData("insuranceData", 300000); // 5 min for insurance
    setInsuranceData(cachedInsurance || []);
    setInsuranceLoading(!!cachedInsurance ? false : true);

    // Fetch fresh data in background
    fetchUserData();
    fetchVehicleHistory();
    fetchCollectionData();
    fetchInsuranceExpiringSoon();
    // Check terms after a delay
    setTimeout(() => {
      checkGarageTerms();
    }, 2000);
  }, []);

  const fetchInsuranceExpiringSoon = async () => {
    try {
      // No setInsuranceLoading(true); - background fetch
      const formData = new FormData();
      formData.append("company_id", "2");
      formData.append("vehicle_grage_id", localStorage.getItem("grage_id"));
      // Calculate -15 days to +30 days date range
      const today = new Date();
      const fromDate = new Date(today);
      fromDate.setDate(today.getDate() - 15);
      const toDate = new Date(today);
      toDate.setDate(today.getDate() + 30);
      formData.append("from_date", fromDate.toISOString().split("T")[0]);
      formData.append("to_date", toDate.toISOString().split("T")[0]);
      formData.append("vehicle_id", "");
      formData.append("insurance_under_sgm", "");
      const response = await axios.post(
        `${server_url}insurance.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (response.data.status === "1") {
        const data = response.data.result || [];
        // Sort by expiry date (closest first)
        const sortedData = data.sort(
          (a, b) =>
            new Date(a.insurance_valid_till) - new Date(b.insurance_valid_till)
        );
        // Take only first 3
        const limitedData = sortedData.slice(0, 3);
        setInsuranceData(limitedData);
        setCachedData("insuranceData", limitedData);
      } else {
        setInsuranceData([]);
        setCachedData("insuranceData", []);
      }
    } catch (error) {
      console.error("Fetch insurance error:", error);
      setInsuranceData([]);
      // Do not cache on error
    } finally {
      setInsuranceLoading(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const form = new FormData();
      form.append("company_id", localStorage.getItem("company_id"));
      form.append("garage_id", localStorage.getItem("grage_id"));
      form.append("version_code", "1.0");
      const webToken = localStorage.getItem("fcm_token");
      if (webToken) {
        form.append("web_token", webToken);
      }
      const response = await axios.post(`${server_url}check_user.php`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const res = response.data;
      if (res.status == "3" || res.status == "0") {
        navigate("/Logout");
        return;
      }
      const user = res.result[0];
      const upiId = user.grage_upi_id || "";
      localStorage.setItem("membership_status", res.membership_status);
      setGarageUPI(upiId);
      // Check if UPI reminder should be shown
      if (ReminderManager.shouldShowUPIReminder(upiId)) {
        setTimeout(() => {
          setShowUPIReminder(true);
          ReminderManager.markReminderShown(ReminderManager.UPI_REMINDER_KEY);
        }, 3000); // Show after 3 seconds
      }
      localStorage.setItem("membership_hide_show", res.membership_hide_show);
      localStorage.setItem("grage_referal_code", user.grage_referal_code);
      localStorage.setItem("grage_id", user.grage_id);
      localStorage.setItem("grage_upi_id", user.grage_upi_id);
      localStorage.setItem("garageLoginNumber", user.grage_number);
      localStorage.setItem("grage_number", user.grage_number);
      localStorage.setItem("grage_name", user.grage_name);
      localStorage.setItem("grage_email", user.grage_email);
      localStorage.setItem("grage_address", user.grage_address);
      localStorage.setItem(
        "grage_contact_person_name",
        user.grage_contact_person_name
      );
      localStorage.setItem("grage_city_id", user.grage_city_id);
      localStorage.setItem("grage_state_id", user.grage_state_id);
      localStorage.setItem("grage_gst_no", user.grage_gst_no);
      const company = res.company_details[0];
      localStorage.setItem("company_store_name", company.company_store_name);
      localStorage.setItem("company_favicon", company.company_favicon);
      localStorage.setItem("company_footer_logo", company.company_footer_logo);
      localStorage.setItem("company_header_logo", company.company_header_logo);
      localStorage.setItem("company_email", company.company_email);
      localStorage.setItem("company_mobile", company.company_mobile);
      localStorage.setItem("company_landline", company.company_landline);
      localStorage.setItem("company_address", company.company_address);
      localStorage.setItem(
        "company_terms_and_condition",
        company.company_terms_and_condition
      );
      localStorage.setItem(
        "company_privacy_policy",
        company.company_privacy_policy
      );
      localStorage.setItem("company_about_us", company.company_about_us);
      localStorage.setItem("company_website", company.company_website);
    } catch (err) {
      navigate("/Logout");
      console.error("check_user error:", err);
    }
  };

  // Add new function to check terms
  const checkGarageTerms = async () => {
    try {
      const form = new FormData();
      form.append("type", 1);
      form.append("garage_id", localStorage.getItem("grage_id"));
      const response = await axios.post(`${server_url}garage_tc.php`, form);
      const hasTerms =
        response.data.status === 1 && response.data.data.length > 0;
      setHasTermsData(hasTerms);
      // Check if terms reminder should be shown
      if (
        ReminderManager.shouldShowTermsReminder(
          hasTerms ? response.data.data : []
        )
      ) {
        setTimeout(() => {
          setShowTermsReminder(true);
          ReminderManager.markReminderShown(ReminderManager.TERMS_REMINDER_KEY);
        }, 5000); // Show after 5 seconds
      }
    } catch (err) {
      console.error("Error checking terms:", err);
    }
  };

  // Add modal handlers
  const handleUPISuccess = (newUPI) => {
    setGarageUPI(newUPI);
    setShowUPIReminder(false);
  };
  const handleTermsSuccess = () => {
    setHasTermsData(true);
    setShowTermsReminder(false);
  };

  const fetchVehicleHistory = async () => {
    try {
      // No setLoading(true); - background fetch
      const form = new FormData();
      form.append("company_id", "2");
      form.append("customer_id", "");
      form.append("vehicle_entry_grage_id", localStorage.getItem("grage_id"));
      form.append("from_date", "");
      form.append("to_date", "");
      const response = await axios.post(
        `${server_url}vehicle_history.php`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const res = response.data;
      if (res.status == "1" && res.result.length > 0) {
        const vehiclesInGarage = res.result.filter(
          (vehicle) =>
            !vehicle.vehicle_entry_out_date && !vehicle.vehicle_entry_out_time
        );
        setJobCards(vehiclesInGarage);
        setCachedData("jobCards", vehiclesInGarage);
      } else {
        setJobCards([]);
        setCachedData("jobCards", []);
      }
    } catch (err) {
      console.error("fetchVehicleHistory error:", err);
      setJobCards([]);
      // Do not cache on error
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionData = async () => {
    try {
      // No setCollectionLoading(true); - background fetch
      const form = new FormData();
      form.append("company_id", "2");
      form.append("garage_id", localStorage.getItem("grage_id"));
      // You'll need to create this API endpoint
      const response = await axios.post(
        `${server_url}get_collection_data.php`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const res = response.data;
      if (res.status === "1") {
        const newData = {
          currentMonth: res.current_month || { totalCars: 0, amount: 0 },
          lastThreeMonths: res.last_three_months || [],
        };
        setCollectionData(newData);
        setCachedData("collectionData", newData);
      } else {
        // Set to default on error
        const defaultData = {
          currentMonth: { totalCars: 0, amount: 0 },
          lastThreeMonths: [],
        };
        setCollectionData(defaultData);
        setCachedData("collectionData", defaultData);
      }
    } catch (err) {
      console.error("fetchCollectionData error:", err);
      // Set demo data for development, but cache it
      const demoData = {
        currentMonth: { totalCars: 12, amount: 45000 },
        lastThreeMonths: [
          { month: "January 2024", totalCars: 15, amount: 52000 },
          { month: "December 2023", totalCars: 18, amount: 61000 },
          { month: "November 2023", totalCars: 14, amount: 48000 },
        ],
      };
      setCollectionData(demoData);
      setCachedData("collectionData", demoData);
    } finally {
      setCollectionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const getStatusColor = (validTill) => {
    if (!validTill) return "#666";
    const today = new Date();
    const expiryDate = new Date(validTill);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "#dc2626"; // Expired - Red
    if (diffDays <= 7) return "#f59e0b"; // Expiring this week - Orange
    if (diffDays <= 30) return "#3b82f6"; // Expiring this month - Blue
    return "#10b981"; // Valid - Green
  };

  const getStatusText = (validTill) => {
    if (!validTill) return "";
    const today = new Date();
    const expiryDate = new Date(validTill);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "Expired";
    if (diffDays === 0) return "Expires Today";
    if (diffDays === 1) return "Expires Tomorrow";
    if (diffDays <= 7) return `${diffDays} days left`;
    if (diffDays <= 30) return "This month";
    return "Valid";
  };

  // Menu icons data
  const menuIcons = [
    {
      icon: server_folder + "/img/menu/AddNewVehicle.png",
      text: "Add job card",
      link: "/Addjobcart",
    },
    {
      icon: server_folder + "/img/menu/Customers.png",
      text: "Customers",
      link: "/customers",
    },
    {
      icon: server_folder + "/img/menu/vehicles.png",
      text: "Vehicles",
      link: "/vehicles",
    },
    {
      icon: server_folder + "/img/menu/History.png",
      text: "Insurance",
      link: "/insurance",
    },
  ];

  // Slider images
  const sliderImages = [
    server_folder + "/img/slide1.jpg",
    server_folder + "/img/slide2.jpg",
  ];

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };

  const getCurrentMonthName = () => {
    return new Date().toLocaleString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  return (
    <>
      {/* Add these modals at the end of your component */}
      {showUPIReminder && (
        <UPIReminderModal
          onClose={() => setShowUPIReminder(false)}
          onSuccess={handleUPISuccess}
        />
      )}
      {showTermsReminder && (
        <TermsReminderModal
          onClose={() => setShowTermsReminder(false)}
          onSuccess={handleTermsSuccess}
        />
      )}
      {/* Hero Slider with Swiper */}
      <div className="img-hero">
        <Swiper
          loop={true}
          pagination={{
            dynamicBullets: true,
            clickable: true,
          }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          modules={[Pagination, Autoplay]}
          className="mySwiper"
        >
          {sliderImages.map((image, index) => (
            <SwiperSlide key={index}>
              <img
                className="banneimage"
                src={image}
                alt={`slider ${index + 1}`}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {/* Section 1 - Menu Icons */}
      <div
        className="home-icon"
        style={{ paddingLeft: "3%", paddingRight: "3%" }}
      >
        <div className="section-home">
          <div className="container">
            <div className="row">
              {menuIcons.slice(0, 4).map((item, index) => (
                <div key={index} className="col s-icon">
                  <Link to={item.link} className="homepage-icon-menu">
                    <img src={item.icon} alt="icon" />
                    <div className="s-icon-text">{item.text}</div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Current Month Collection Card */}
      <div
        className="home-icon"
        style={{ paddingLeft: "3%", marginTop: "15px", paddingRight: "3%" }}
      >
        {collectionLoading ? (
          <div className="collection-loading">
            <div className="loading-spinner small"></div>
            <span>Loading collection data...</span>
          </div>
        ) : (
          <div className="collection-stats-container">
            {/* Received (This Month) */}
            <div className="collection-item" style={{ background: "#d6e7e5" }}>
              <div
                className="collection-icon-wrapper"
                style={{ background: "rgb(111 194 184)" }}
              >
                <i
                  className="fas fa-rupee-sign collection-icon"
                  style={{ color: "white" }}
                ></i>
              </div>
              <div className="collection-content">
                <div className="collection-amount">
                  {formatAmount(collectionData.currentMonth.amount)}
                </div>
                <div className="collection-title">Received (This Month)</div>
              </div>
            </div>
            {/* Pending (Total) */}
            <div
              className="collection-item"
              style={{ background: "#e7e2e6", marginLeft: "20px" }}
            >
              <div
                className="collection-icon-wrapper"
                style={{ background: "rgb(183 151 172)" }}
              >
                <i
                  className="fas fa-clock collection-icon"
                  style={{ color: "white" }}
                ></i>
              </div>
              <div className="collection-content">
                <div className="collection-amount">
                  {formatAmount(collectionData.currentMonth.pendingAmount || 0)}
                </div>
                <div className="collection-title">Pending</div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Last 3 Months Collection Table - Only show if there's data with amount > 0 */}
      {collectionData?.lastThreeMonths?.some(
        (monthData) => monthData.amount > 0
      ) && (
        <div>
          <div className="heading-section">
            <div className="sa-title popcat">
              {(() => {
                const validMonths = collectionData.lastThreeMonths.filter(
                  (m) => m.amount > 0
                ).length;
                if (validMonths === 3) {
                  return "Last 3 Months Overview";
                } else if (validMonths === 2) {
                  return "Last 2 Months Overview";
                } else if (validMonths === 1) {
                  return "Last Month Overview";
                }
                return "Recent Collections"; // Fallback
              })()}
            </div>
          </div>
          <div
            className="home-icon"
            style={{ paddingLeft: "3%", marginTop: "15px", paddingRight: "3%" }}
          >
            <div className="collection-table">
              {collectionLoading ? (
                <div className="collection-loading">
                  <div className="loading-spinner small"></div>
                  <span>Loading history...</span>
                </div>
              ) : (
                <div className="table-container">
                  <table className="months-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left" }} className="month-col">
                          <b>Month</b>
                        </th>
                        <th
                          style={{ textAlign: "center" }}
                          className="cars-col"
                        >
                          <b>Cars</b>
                        </th>
                        <th
                          style={{ textAlign: "right" }}
                          className="amount-col"
                        >
                          <b>Amount</b>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {collectionData.lastThreeMonths
                        .filter((monthData) => monthData.amount > 0)
                        .map((monthData, index) => (
                          <tr
                            key={index}
                            className={index % 2 === 0 ? "even-row" : "odd-row"}
                          >
                            <td className="month-name">{monthData.month}</td>
                            <td className="cars-count">
                              {monthData.totalCars}
                            </td>
                            <td className="amount-value">
                              {formatAmount(monthData.amount)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {insuranceData.length > 0 && (
        <div className="heading-section">
          <div className="sa-title popcat">Insurance Expiring Soon</div>
          <Link to="/insurance" className="view-all-btn">
            View All
          </Link>
        </div>
      )}

      {insuranceData.length > 0 && (
        <div
          className="home-icon"
          style={{ paddingLeft: "3%", marginTop: "5px", paddingRight: "3%" }}
        >
          <div className="insurance-horizontal-scroll">
            {insuranceLoading ? (
              <div className="insurance-loading">
                <div className="loading-spinner small"></div>
                <span>Loading insurance data...</span>
              </div>
            ) : (
              <div className="insurance-cards-container">
                {insuranceData.map((insurance, index) => (
                  <div
                    key={index}
                    className="insurance-card"
                    style={{
                      borderLeft:
                        "4px solid " +
                        getStatusColor(insurance.insurance_valid_till),
                    }}
                  >
                    <div
                      className="insurance-header"
                      onClick={() => handleEditInsurance(insurance)}
                    >
                      <div className="vehicle-reg">
                        <b>
                          {insurance.vehicle_reg_no || ""}{" "}
                          {insurance?.brand_name || insurance.brand_name || ""}
                          {insurance?.model_name || insurance.model_name
                            ? ` ${
                                insurance?.model_name || insurance.model_name
                              }`
                            : ""}
                        </b>
                      </div>
                      <div
                        className="insurance-status"
                        style={{
                          color: getStatusColor(insurance.insurance_valid_till),
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                      >
                        {getStatusText(insurance.insurance_valid_till)}
                      </div>
                    </div>
                    <div className="insurance-details">
                      <div className="policy-no">
                        <i
                          className="fas fa-user"
                          style={{ marginRight: "5px" }}
                        ></i>
                        {insurance.customer_name}
                        <a
                          style={{
                            marginLeft: "2px",
                            textDecoration: "underline",
                          }}
                          href={`tel:${insurance.customer_mobile}`}
                        >
                          {insurance.customer_mobile &&
                            ` (${insurance.customer_mobile})`}
                        </a>
                      </div>
                      <div
                        className="company-name"
                        onClick={() => handleEditInsurance(insurance)}
                      >
                        <i
                          className="fas fa-building"
                          style={{ marginRight: "5px" }}
                        ></i>
                        <p style={{ width: "100%", marginBottom: "0px" }}>
                          {insurance.insurance_company_name || ""}{" "}
                          <span style={{ float: "right" }}>
                            {insurance.insurance_policy_number && (
                              <>No: {insurance.insurance_policy_number}</>
                            )}
                          </span>
                        </p>
                      </div>
                      <div
                        className="validity"
                        onClick={() => handleEditInsurance(insurance)}
                      >
                        <i
                          className="fas fa-calendar-alt"
                          style={{ marginRight: "5px" }}
                        ></i>
                        <p style={{ width: "100%", marginBottom: "0px" }}>
                          {formatDate(insurance.insurance_start_date)} -{" "}
                          {formatDate(insurance.insurance_valid_till)}
                          <span
                            className="insurance-amount"
                            style={{ float: "right" }}
                          >
                            ₹
                            {parseFloat(
                              insurance.insurance_amount || 0
                            ).toLocaleString("en-IN")}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Section 4 - Vehicle in Garage */}
      <div className="heading-section">
        <div className="sa-title popcat">Vehicle In Garage</div>
        <div className="clear"></div>
      </div>
      <div className="home-vehicles">
        {loading ? (
          <div className="loading-vehicles">
            <div className="loading-spinner"></div>
            <div>Loading vehicles...</div>
          </div>
        ) : jobCards.length > 0 ? (
          <div className="vehicles-list home-icon">
            {jobCards.map(
              (vehicle, index) =>
                !vehicle.vehicle_entry_out_date && (
                  <div key={index} className="vehicle-card section-home">
                    <div className="vehicle-header">
                      <Link
                        to={`/job-card-details/${vehicle.vehicle_entry_id}`}
                        state={{ vehicle: vehicle }}
                        style={{ width: "100%" }}
                      >
                        <table style={{ width: "100%", textAlign: "left" }}>
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  width: "50%",
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                }}
                              >
                                <>
                                  <span>#{vehicle.vehicle_entry_no}</span>{" "}
                                  {vehicle.customer_name || ""}
                                </>
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  fontWeight: "bold",
                                  color: "#0352a1",
                                }}
                              >
                                {vehicle.customer_mobile || ""}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ width: "50%", color: "#2c3e50" }}>
                                {vehicle.vehicle_reg_no || ""}
                              </td>
                              <td style={{ textAlign: "right", color: "#666" }}>
                                {vehicle.brand_name || ""}{" "}
                                {vehicle.model_name || ""}
                              </td>
                            </tr>
                            <tr>
                              <td
                                style={{
                                  width: "50%",
                                  fontSize: "12px",
                                  color: "#666",
                                }}
                              >
                                Entry: {vehicle.vehicle_entry_in_date}{" "}
                                {vehicle.vehicle_entry_in_time}
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  fontWeight: "bold",
                                  color: "#27ae60",
                                }}
                              >
                                {formatAmount(vehicle.vehicle_entry_total_amt)}
                              </td>
                            </tr>
                            {vehicle.vehicle_entry_remark && (
                              <tr>
                                <td
                                  colSpan="2"
                                  style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    fontStyle: "italic",
                                    paddingTop: "8px",
                                  }}
                                >
                                  📝 {vehicle.vehicle_entry_remark}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Link>
                    </div>
                  </div>
                )
            )}
          </div>
        ) : (
          <div className="no-vehicles section-home">
            <div className="no-vehicles-icon">🚗</div>
            <div className="no-vehicles-title">No Active Vehicles</div>
            <div className="no-vehicles-text">
              No vehicles currently in service
            </div>
          </div>
        )}
      </div>
      {/* Floating Create Job Card Button */}
      <div className="floating-create-btn-container">
        <Link to="/Addjobcart" className="floating-create-btn">
          <i className="fas fa-plus-circle"></i>
          <span className="btn-text">Create Job Card</span>
        </Link>
      </div>
      {/* Add Collection Styles */}
      <style jsx>{`
        /* Horizontal Scroll Insurance Section */
        .insurance-horizontal-scroll {
          position: relative;
        }

        .insurance-cards-container {
          display: flex;
          gap: 12px;
          overflow-x: auto;
          padding: 8px 0;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
        }

        /* Hide scrollbar but keep functionality */
        .insurance-cards-container::-webkit-scrollbar {
          display: none;
        }
        .insurance-cards-container {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }

        .insurance-card {
          background: white;
          border-radius: 12px;
          padding: 15px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          flex: 0 0 320px; /* Fixed width for cards */
          min-width: 280px;
          max-width: 320px;
        }

        .insurance-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Optional: Add subtle scroll indicator on mobile */
        .insurance-horizontal-scroll::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 30px;
          height: 100%;
          background: linear-gradient(to left, white, transparent);
          pointer-events: none;
          opacity: 0.7;
        }

        /* Responsive adjustments */
        @media (max-width: 480px) {
          .insurance-card {
            flex: 0 0 320px;
            min-width: 340px;
            padding: 12px;
          }

          .vehicle-reg {
            font-size: 14px;
          }

          .insurance-details div {
            font-size: 12px;
          }
        }

        .collection-stats-container {
          display: ruby;
          gap: 15px;
          justify-content: space-between;
        }
        .collection-item {
          flex: 1;
          width: 46%;
          display: revert;
          align-items: center;
          padding: 15px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          min-height: 90px;
        }
        .collection-icon-wrapper {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          flex-shrink: 0;
        }
        .collection-icon {
          font-size: 22px;
          color: #2c3e50;
        }
        .collection-content {
          margin-top: 40px;
          flex: 1;
        }
        .collection-amount {
          font-size: 22px;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 5px;
          line-height: 1.2;
        }
        .collection-title {
          font-size: 13px;
          color: #666;
          font-weight: 500;
          line-height: 1.3;
        }
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .collection-stats-container {
            flex-direction: column;
            gap: 12px;
          }
          .collection-item {
            min-height: 80px;
            padding: 12px;
            width: 46%;
          }
          .collection-icon-wrapper {
            width: 45px;
            height: 45px;
            margin-right: 12px;
          }
          .collection-icon {
            font-size: 20px;
          }
          .collection-amount {
            font-size: 20px;
          }
          .collection-title {
            font-size: 12px;
          }
        }
        @media (max-width: 480px) {
          .collection-item {
            min-height: 70px;
            padding: 15px;
            width: 46%;
          }
          .collection-icon-wrapper {
            width: 40px;
            height: 40px;
            margin-right: 10px;
          }
          .collection-icon {
            font-size: 18px;
          }
          .collection-amount {
            font-size: 18px;
          }
        }
        /* Insurance Section Styles */
        .view-all-btn {
          background: #0352a1;
          color: white;
          padding: 5px 15px;
          border-radius: 20px;
          text-decoration: none;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.3s ease;
          width: 93px;
        }
        .view-all-btn:hover {
          background: #023e7d;
          transform: translateY(-1px);
        }
        .insurance-list {
          display: grid;
          gap: 12px;
        }
        .insurance-card {
          background: white;
          border-radius: 12px;
          padding: 15px;
          border-left: 4px solid #0352a1;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }
        .insurance-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .insurance-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        .vehicle-reg {
          font-weight: 600;
          font-size: 15px;
          color: #2c3e50;
        }
        .insurance-status {
          font-size: 11px;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
        }
        .insurance-details {
          margin-bottom: 10px;
        }
        .insurance-details div {
          font-size: 13px;
          color: #666;
          margin-bottom: 5px;
          display: flex;
          align-items: center;
        }
        .insurance-details i {
          width: 15px;
          text-align: center;
        }
        .insurance-amount {
          font-weight: 600;
          color: #27ae60;
        }
        .insurance-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          color: #666;
          background: white;
          border-radius: 12px;
          border: 1px solid #e0e0e0;
        }
        /* Responsive styles */
        @media (max-width: 768px) {
          .insurance-card {
            padding: 12px;
          }
          .vehicle-reg {
            font-size: 14px;
          }
          .insurance-details div {
            font-size: 12px;
          }
        }
        /* Floating Create Job Card Button */
        .floating-create-btn-container {
          position: fixed;
          bottom: 80px;
          left: 0;
          right: 12px;
          display: flex;
          justify-content: right;
          z-index: 1000;
          pointer-events: none;
        }
        .floating-create-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          background: linear-gradient(90deg, #e85a41 0%, #e88a4b 100%);
          color: white;
          padding: 14px 24px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          font-size: 16px;
          box-shadow: 0 4px 20px rgba(3, 82, 161, 0.4);
          transition: all 0.3s ease;
          pointer-events: auto;
          border: 2px solid white;
        }
        .floating-create-btn:active {
          transform: translateY(0);
        }
        .floating-create-btn i {
          font-size: 20px;
        }
        .btn-text {
          letter-spacing: 0.5px;
        }
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .floating-create-btn-container {
            bottom: 75px;
          }
          .floating-create-btn {
            padding: 12px 20px;
            font-size: 15px;
          }
          .floating-create-btn i {
            font-size: 18px;
          }
        }
        @media (max-width: 480px) {
          .floating-create-btn {
            padding: 10px 18px;
            font-size: 14px;
            gap: 8px;
          }
          .btn-text {
            display: block; /* Always show text on mobile */
          }
        }
        .home-icon .section-home {
          padding: 3% 3%;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }
        /* Collection Card Styles */
        .collection-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px;
          padding: 10px;
          color: white;
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
          position: relative;
          overflow: hidden;
        }
        .collection-card.current-month {
          background: linear-gradient(90deg, #d6e7e5 50%, #e7e2e6 50%);
          color: black;
        }
        .collection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        .month-title {
          font-size: 18px;
          font-weight: 600;
        }
        .collection-badge {
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          backdrop-filter: blur(10px);
        }
        .collection-stats {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }
        .stat-divider {
          width: 1px;
          height: 40px;
          background: rgba(255, 255, 255, 0.3);
          margin: 0 20px;
        }
        .stat-label {
          font-size: 12px;
          opacity: 0.9;
          margin-bottom: 5px;
        }
        .stat-value {
          font-size: 24px;
          font-weight: 700;
        }
        .collection-progress {
          height: 6px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
          overflow: hidden;
        }
        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #4cd964, #34e89e);
          border-radius: 3px;
          transition: width 0.5s ease;
        }
        /* Collection Table Styles */
        .collection-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 3px 3px 20px 0px #00000036;
        }
        .table-container {
          overflow-x: auto;
        }
        .months-table {
          width: 100%;
          border-collapse: collapse;
        }
        .months-table th {
          background: #f8f9fa;
          padding: 15px 12px;
          text-align: center;
          font-weight: 600;
          color: #2c3e50;
          border-bottom: 2px solid #e9ecef;
          font-size: 14px;
        }
        .months-table td {
          padding: 15px 12px;
          border-bottom: 1px solid #e9ecef;
          font-size: 14px;
        }
        .month-col {
          text-align: left;
          width: 50%;
        }
        .cars-col {
          width: 20%;
          text-align: center;
        }
        .amount-col {
          width: 30%;
          text-align: right;
        }
        .month-name {
          text-align: left;
          font-weight: 500;
          color: #2c3e50;
        }
        .cars-count {
          text-align: center;
          font-weight: 600;
          color: #0352a1;
        }
        .amount-value {
          text-align: right;
          font-weight: 600;
          color: #27ae60;
        }
        .even-row {
          background: #fafbfc;
        }
        .odd-row {
          background: white;
        }
        .even-row:hover,
        .odd-row:hover {
          background: #f1f3f4;
        }
        /* Loading States */
        .collection-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 30px;
          color: #666;
        }
        .loading-spinner.small {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 10px;
        }
        .collection-loading span {
          font-size: 14px;
        }
        .no-data-message {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }
        .no-data-icon {
          font-size: 48px;
          margin-bottom: 15px;
          opacity: 0.5;
        }
        .no-data-text {
          font-size: 14px;
        }
        /* Responsive Design */
        @media (max-width: 768px) {
          .collection-card {
            padding: 12px;
          }
          .stat-value {
            font-size: 20px;
          }
          .month-title {
            font-size: 16px;
          }
          .months-table th,
          .months-table td {
            padding: 12px 8px;
            font-size: 13px;
          }
          .month-col {
            text-align: left;
            width: 40%;
          }
          .cars-col {
            width: 25%;
          }
          .amount-col {
            width: 35%;
          }
        }
        @media (max-width: 480px) {
          .collection-stats {
            flex-direction: column;
            gap: 10px;
          }
          .stat-divider {
            display: none;
          }
          .months-table {
            font-size: 12px;
          }
          .months-table th,
          .months-table td {
            padding: 10px 10px 10px 10px;
          }
        }
        /* Existing styles remain the same */
        .img-hero {
          position: relative;
        }
        .swiper {
          width: 100%;
          height: 100%;
        }
        .swiper-slide {
          text-align: center;
          font-size: 18px;
          background: #fff;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .swiper-slide img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .swiper-pagination-bullet {
          width: 8px;
          height: 8px;
          background: rgba(255, 255, 255, 0.5);
          opacity: 1;
        }
        .swiper-pagination-bullet-active {
          background: #fff;
        }
        .heading-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding: 0 20px;
          margin-top: 15px;
        }
        .vehicle-count {
          color: #ffffff;
          background-color: #0352a1;
          font-size: 14px;
          font-weight: 500;
          padding: 5px 10px;
          border-radius: 50px;
          height: 32px;
        }
        .home-vehicles {
          padding: 0 20px 20px;
        }
        .loading-vehicles {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }
        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .vehicles-list {
          display: grid;
          gap: 15px;
        }
        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #0352a1;
          transition: all 0.3s ease;
        }
        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 20px 0;
        }
        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }
        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
        }
        .add-vehicle-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e74c3c;
          color: white;
          padding: 10px 20px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .add-vehicle-btn:hover {
          background: #c0392b;
          transform: translateY(-1px);
        }
        @media (max-width: 768px) {
          .heading-section {
            padding: 0 15px;
          }
          .home-vehicles {
            padding: 0 15px 60px;
          }
          .vehicle-card {
            padding: 12px;
          }
        }
      `}</style>
    </>
  );
};

export default Home;
